import localforage from 'localforage'
import type { AppliedCoupon, CartLine, OfflineCacheScope } from 'store/posStore'
import { resolveOfflineScope, scopedPosKey } from 'store/posStore'

const idb = localforage.createInstance({
  name: 'erp-pos',
  storeName: 'pos',
})

export const PENDING_TAKEAWAY_JOURNAL_INDEX_KEY = 'pos_pending_takeaway_journal_index'
export const PENDING_TAKEAWAY_JOURNAL_SCHEMA_VERSION = 1

export type PendingTakeawayJournalAction =
  | 'CREATE_PENDING'
  | 'UPDATE_PENDING'
  | 'KITCHEN_DISPATCH_INTENT'
  | 'KITCHEN_PRINT_ACK'
  | 'KITCHEN_PRINT_UNCERTAIN'
  | 'SETTLE_PENDING'
  | 'CANCEL_PENDING'

export type PendingTakeawayOpSyncStatus = 'pending' | 'syncing' | 'synced' | 'conflict' | 'failed'

export type PendingTakeawayLocalStatus =
  | 'LOCAL_PENDING_UNPAID'
  | 'LOCAL_KITCHEN_PENDING'
  | 'LOCAL_KITCHEN_SENT'
  | 'LOCAL_PENDING_EDITED'
  | 'LOCAL_KITCHEN_DELTA'
  | 'LOCAL_SETTLED_UNSYNCED'
  | 'LOCAL_CANCELLED_UNSYNCED'
  | 'SYNCED'
  | 'CONFLICT_NEEDS_REVIEW'

export type PendingTakeawayJournalOp = {
  clientOpId: string
  seq: number
  action: PendingTakeawayJournalAction
  clientUuid: string
  tenantId: string
  branchId: string
  posRegisterId: string | null
  registerSessionId: string | null
  shiftId: string | null
  cashierUserId: number | string | null
  payload: Record<string, unknown>
  syncStatus: PendingTakeawayOpSyncStatus
  serverSaleId: number | null
  expectedCartRevision: number | null
  retryCount: number
  lastError: string | null
  conflictCode: string | null
  createdAt: string
  syncedAt: string | null
}

export type PendingTakeawayJournalOrder = {
  clientUuid: string
  tenantId: string
  branchId: string
  origin: 'local' | 'server'
  saleId: number | null
  localStatus: PendingTakeawayLocalStatus
  cartRevision: number | null
  posRegisterId: string | null
  registerSessionId: string | null
  shiftId: string | null
  cashierUserId: number | string | null
  nextSeq: number
  kitchenUncertain: boolean
  snapshot: {
    lines: CartLine[]
    orderNotes: string
    cartDiscount: number
    customer: unknown
    appliedCoupon: AppliedCoupon | null
  }
  ops: PendingTakeawayJournalOp[]
  updatedAt: string
}

const locks = new Map<string, Promise<unknown>>()

function journalIndexKey(scope?: OfflineCacheScope): string {
  return scopedPosKey(PENDING_TAKEAWAY_JOURNAL_INDEX_KEY, scope, 'branch')
}

function journalOrderKey(clientUuid: string, scope?: OfflineCacheScope): string {
  return scopedPosKey(`pos_pending_takeaway_journal:${clientUuid}`, scope, 'branch')
}

function isOrder(value: unknown): value is PendingTakeawayJournalOrder {
  if (!value || typeof value !== 'object') return false
  const rec = value as PendingTakeawayJournalOrder
  return typeof rec.clientUuid === 'string' && rec.clientUuid.length > 0 && Array.isArray(rec.ops)
}

async function withOrderLock<T>(clientUuid: string, fn: () => Promise<T>): Promise<T> {
  const prior = locks.get(clientUuid) ?? Promise.resolve()
  let release: () => void = () => undefined
  const current = new Promise<void>((resolve) => {
    release = resolve
  })
  const chained = prior.then(() => current)
  locks.set(clientUuid, chained)
  await prior.catch(() => undefined)
  try {
    return await fn()
  } finally {
    release()
    if (locks.get(clientUuid) === chained) locks.delete(clientUuid)
  }
}

async function readIndex(scope?: OfflineCacheScope): Promise<string[]> {
  const raw = await idb.getItem(journalIndexKey(scope))
  if (!Array.isArray(raw)) return []
  return raw.filter((id): id is string => typeof id === 'string' && id.length > 0)
}

async function writeIndex(ids: string[], scope?: OfflineCacheScope): Promise<void> {
  const unique = Array.from(new Set(ids))
  await idb.setItem(journalIndexKey(scope), unique)
}

export function mapJournalActionToSyncAction(
  action: PendingTakeawayJournalAction,
): 'create' | 'update' | 'kitchen_ack' | 'settle' | 'cancel' | null {
  switch (action) {
    case 'CREATE_PENDING':
      return 'create'
    case 'UPDATE_PENDING':
      return 'update'
    case 'KITCHEN_PRINT_ACK':
    case 'KITCHEN_PRINT_UNCERTAIN':
      return 'kitchen_ack'
    case 'SETTLE_PENDING':
      return 'settle'
    case 'CANCEL_PENDING':
      return 'cancel'
    case 'KITCHEN_DISPATCH_INTENT':
      return null
    default:
      return null
  }
}

export function nextLocalStatusForAction(
  current: PendingTakeawayLocalStatus,
  action: PendingTakeawayJournalAction,
): PendingTakeawayLocalStatus {
  if (current === 'CONFLICT_NEEDS_REVIEW') return current
  switch (action) {
    case 'CREATE_PENDING':
      return 'LOCAL_PENDING_UNPAID'
    case 'UPDATE_PENDING':
      return current === 'LOCAL_KITCHEN_SENT' || current === 'LOCAL_KITCHEN_DELTA'
        ? 'LOCAL_PENDING_EDITED'
        : current === 'LOCAL_PENDING_UNPAID'
          ? 'LOCAL_PENDING_UNPAID'
          : 'LOCAL_PENDING_EDITED'
    case 'KITCHEN_DISPATCH_INTENT':
      return 'LOCAL_KITCHEN_PENDING'
    case 'KITCHEN_PRINT_ACK':
      return current === 'LOCAL_PENDING_EDITED' || current === 'LOCAL_KITCHEN_DELTA'
        ? 'LOCAL_KITCHEN_DELTA'
        : 'LOCAL_KITCHEN_SENT'
    case 'KITCHEN_PRINT_UNCERTAIN':
      return 'LOCAL_KITCHEN_PENDING'
    case 'SETTLE_PENDING':
      return 'LOCAL_SETTLED_UNSYNCED'
    case 'CANCEL_PENDING':
      return 'LOCAL_CANCELLED_UNSYNCED'
    default:
      return current
  }
}

export async function getPendingTakeawayJournalOrder(
  clientUuid: string,
  scope?: OfflineCacheScope,
): Promise<PendingTakeawayJournalOrder | null> {
  const resolved = resolveOfflineScope(scope)
  const raw = await idb.getItem(journalOrderKey(clientUuid, resolved))
  if (!isOrder(raw)) return null
  if (raw.branchId && resolved.branchId && raw.branchId !== resolved.branchId) return null
  if (raw.tenantId && resolved.tenantId && raw.tenantId !== resolved.tenantId) return null
  return raw
}

export async function listPendingTakeawayJournalOrders(
  scope?: OfflineCacheScope,
): Promise<PendingTakeawayJournalOrder[]> {
  const resolved = resolveOfflineScope(scope)
  const ids = await readIndex(resolved)
  const orders: PendingTakeawayJournalOrder[] = []
  for (const id of ids) {
    const order = await getPendingTakeawayJournalOrder(id, resolved)
    if (order) orders.push(order)
  }
  return orders
}

export async function countUnsyncedPendingTakeawayJournal(
  scope?: OfflineCacheScope,
): Promise<{ pending: number; conflict: number; settledUnsynced: number }> {
  const orders = await listPendingTakeawayJournalOrders(scope)
  let pending = 0
  let conflict = 0
  let settledUnsynced = 0
  for (const order of orders) {
    if (order.localStatus === 'CONFLICT_NEEDS_REVIEW' || order.ops.some((op) => op.syncStatus === 'conflict')) {
      conflict += 1
      continue
    }
    const unsynced = order.ops.some((op) => op.syncStatus === 'pending' || op.syncStatus === 'failed' || op.syncStatus === 'syncing')
    if (!unsynced || order.localStatus === 'SYNCED') continue
    pending += 1
    if (order.localStatus === 'LOCAL_SETTLED_UNSYNCED') settledUnsynced += 1
  }
  return { pending, conflict, settledUnsynced }
}

export async function ensurePendingTakeawayJournalOrder(params: {
  clientUuid: string
  tenantId: string
  branchId: string
  origin?: 'local' | 'server'
  saleId?: number | null
  posRegisterId?: string | null
  registerSessionId?: string | null
  shiftId?: string | null
  cashierUserId?: number | string | null
  snapshot: PendingTakeawayJournalOrder['snapshot']
  scope?: OfflineCacheScope
}): Promise<PendingTakeawayJournalOrder> {
  return withOrderLock(params.clientUuid, async () => {
    const resolved = resolveOfflineScope({
      ...params.scope,
      branchId: params.branchId,
      tenantId: params.tenantId,
    })
    const existing = await getPendingTakeawayJournalOrder(params.clientUuid, resolved)
    if (existing) return existing
    const created: PendingTakeawayJournalOrder = {
      clientUuid: params.clientUuid,
      tenantId: params.tenantId || resolved.tenantId || '',
      branchId: params.branchId,
      origin: params.origin ?? 'local',
      saleId: params.saleId ?? null,
      localStatus: 'LOCAL_PENDING_UNPAID',
      cartRevision: null,
      posRegisterId: params.posRegisterId ?? null,
      registerSessionId: params.registerSessionId ?? null,
      shiftId: params.shiftId ?? null,
      cashierUserId: params.cashierUserId ?? null,
      nextSeq: 0,
      kitchenUncertain: false,
      snapshot: params.snapshot,
      ops: [],
      updatedAt: new Date().toISOString(),
    }
    await idb.setItem(journalOrderKey(params.clientUuid, resolved), created)
    const ids = await readIndex(resolved)
    if (!ids.includes(params.clientUuid)) {
      await writeIndex([...ids, params.clientUuid], resolved)
    }
    return created
  })
}

export async function appendPendingTakeawayJournalOp(params: {
  clientUuid: string
  action: PendingTakeawayJournalAction
  payload: Record<string, unknown>
  clientOpId: string
  expectedCartRevision?: number | null
  snapshot?: PendingTakeawayJournalOrder['snapshot']
  saleId?: number | null
  posRegisterId?: string | null
  registerSessionId?: string | null
  shiftId?: string | null
  cashierUserId?: number | string | null
  scope?: OfflineCacheScope
}): Promise<PendingTakeawayJournalOrder> {
  if (!params.clientOpId) {
    throw new Error('pending_takeaway_client_op_required')
  }
  return withOrderLock(params.clientUuid, async () => {
    const resolved = resolveOfflineScope(params.scope)
    const existing = await getPendingTakeawayJournalOrder(params.clientUuid, resolved)
    if (!existing) {
      throw new Error('pending_takeaway_journal_missing')
    }
    if (existing.localStatus === 'LOCAL_SETTLED_UNSYNCED' && params.action === 'CANCEL_PENDING') {
      throw new Error('pending_takeaway_cannot_cancel_settled')
    }
    const duplicate = existing.ops.find((op) => op.clientOpId === params.clientOpId)
    if (duplicate) return existing

    const op: PendingTakeawayJournalOp = {
      clientOpId: params.clientOpId,
      seq: existing.nextSeq,
      action: params.action,
      clientUuid: params.clientUuid,
      tenantId: existing.tenantId,
      branchId: existing.branchId,
      posRegisterId: params.posRegisterId ?? existing.posRegisterId,
      registerSessionId: params.registerSessionId ?? existing.registerSessionId,
      shiftId: params.shiftId ?? existing.shiftId,
      cashierUserId: params.cashierUserId ?? existing.cashierUserId,
      payload: params.payload,
      syncStatus: 'pending',
      serverSaleId: params.saleId ?? existing.saleId,
      expectedCartRevision: params.expectedCartRevision ?? existing.cartRevision,
      retryCount: 0,
      lastError: null,
      conflictCode: null,
      createdAt: new Date().toISOString(),
      syncedAt: null,
    }
    const next: PendingTakeawayJournalOrder = {
      ...existing,
      saleId: params.saleId ?? existing.saleId,
      snapshot: params.snapshot ?? existing.snapshot,
      posRegisterId: op.posRegisterId,
      registerSessionId: op.registerSessionId,
      shiftId: op.shiftId,
      cashierUserId: op.cashierUserId,
      nextSeq: existing.nextSeq + 1,
      localStatus: nextLocalStatusForAction(existing.localStatus, params.action),
      kitchenUncertain: params.action === 'KITCHEN_PRINT_UNCERTAIN'
        ? true
        : params.action === 'KITCHEN_PRINT_ACK'
          ? false
          : existing.kitchenUncertain,
      ops: [...existing.ops, op],
      updatedAt: new Date().toISOString(),
    }
    await idb.setItem(journalOrderKey(params.clientUuid, resolved), next)
    const ids = await readIndex(resolved)
    if (!ids.includes(params.clientUuid)) {
      await writeIndex([...ids, params.clientUuid], resolved)
    }
    return next
  })
}

export async function markPendingTakeawayJournalOps(params: {
  clientUuid: string
  clientOpIds: string[]
  syncStatus: PendingTakeawayOpSyncStatus
  saleId?: number | null
  cartRevision?: number | null
  error?: string | null
  conflictCode?: string | null
  localStatus?: PendingTakeawayLocalStatus
  scope?: OfflineCacheScope
}): Promise<PendingTakeawayJournalOrder | null> {
  return withOrderLock(params.clientUuid, async () => {
    const resolved = resolveOfflineScope(params.scope)
    const existing = await getPendingTakeawayJournalOrder(params.clientUuid, resolved)
    if (!existing) return null
    const ids = new Set(params.clientOpIds)
    const now = new Date().toISOString()
    const ops = existing.ops.map((op) => {
      if (!ids.has(op.clientOpId)) return op
      return {
        ...op,
        syncStatus: params.syncStatus,
        serverSaleId: params.saleId ?? op.serverSaleId,
        lastError: params.error ?? (params.syncStatus === 'synced' ? null : op.lastError),
        conflictCode: params.conflictCode ?? (params.syncStatus === 'synced' ? null : op.conflictCode),
        retryCount: params.syncStatus === 'failed' ? op.retryCount + 1 : op.retryCount,
        syncedAt: params.syncStatus === 'synced' ? now : op.syncedAt,
      }
    })
    const allSynced = ops.every((op) => op.syncStatus === 'synced' || op.action === 'KITCHEN_DISPATCH_INTENT')
    const next: PendingTakeawayJournalOrder = {
      ...existing,
      saleId: params.saleId ?? existing.saleId,
      cartRevision: params.cartRevision ?? existing.cartRevision,
      localStatus: params.localStatus
        ?? (params.syncStatus === 'conflict'
          ? 'CONFLICT_NEEDS_REVIEW'
          : allSynced
            ? 'SYNCED'
            : existing.localStatus),
      ops,
      updatedAt: now,
    }
    await idb.setItem(journalOrderKey(params.clientUuid, resolved), next)
    return next
  })
}

export async function recoverHangingKitchenIntent(
  clientUuid: string,
  scope?: OfflineCacheScope,
): Promise<PendingTakeawayJournalOrder | null> {
  const existing = await getPendingTakeawayJournalOrder(clientUuid, scope)
  if (!existing) return null
  const lastKitchen = [...existing.ops].reverse().find((op) =>
    op.action === 'KITCHEN_DISPATCH_INTENT'
    || op.action === 'KITCHEN_PRINT_ACK'
    || op.action === 'KITCHEN_PRINT_UNCERTAIN',
  )
  if (!lastKitchen || lastKitchen.action !== 'KITCHEN_DISPATCH_INTENT') return existing
  return appendPendingTakeawayJournalOp({
    clientUuid,
    action: 'KITCHEN_PRINT_UNCERTAIN',
    clientOpId: `${lastKitchen.clientOpId}:uncertain`,
    payload: {
      ...lastKitchen.payload,
      outcome: 'uncertain',
      recovered_from_intent: true,
    },
    scope,
  })
}

export function unsyncedJournalOps(order: PendingTakeawayJournalOrder): PendingTakeawayJournalOp[] {
  return order.ops.filter((op) => {
    if (op.action === 'KITCHEN_DISPATCH_INTENT') return false
    if (op.syncStatus === 'synced') return false
    if (op.syncStatus === 'conflict') return false
    return true
  })
}

export async function resetPendingTakeawayJournalForTests(): Promise<void> {
  locks.clear()
}
